import React, {
  useState,
  useContext,
  useMemo,
  useCallback,
  useEffect,
  useRef
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import { Copy } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { AppContext } from '../App';
import authService from '../services/authService';
import config from '../config';
import reportingService from '../services/reportingService';
import { formatNumber } from '../utils';

const DATE_PRESETS = [
  { value: 'LAST_7_DAYS', label: 'Last 7 days', days: 6 },
  { value: 'LAST_30_DAYS', label: 'Last 30 days', days: 29 },
  { value: 'LAST_60_DAYS', label: 'Last 60 days', days: 59 },
  { value: 'LAST_90_DAYS', label: 'Last 90 days', days: 89 },
  { value: 'CUSTOM', label: 'Custom range' }
];

const MAX_WINDOW_DAYS = 90;
const DEFAULT_PAGE_SIZE = 100;

const selectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: '#020617',
    borderColor: state.isFocused ? '#334155' : '#1e293b',
    color: '#e2e8f0',
    minHeight: 40,
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#334155'
    }
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#020617',
    border: '1px solid #1e293b',
    color: '#e2e8f0'
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? '#1e293b'
      : state.isFocused
      ? '#0f172a'
      : 'transparent',
    color: '#e2e8f0',
    '&:active': {
      backgroundColor: '#1e293b'
    }
  }),
  singleValue: (base) => ({
    ...base,
    color: '#e2e8f0'
  }),
  input: (base) => ({
    ...base,
    color: '#e2e8f0'
  }),
  placeholder: (base) => ({
    ...base,
    color: '#64748b'
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? '#94a3b8' : '#64748b',
    '&:hover': {
      color: '#94a3b8'
    }
  }),
  clearIndicator: (base) => ({
    ...base,
    color: '#64748b',
    '&:hover': {
      color: '#f87171'
    }
  }),
  indicatorSeparator: () => ({
    display: 'none'
  })
};

const startOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const toISODate = (date) => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy.toISOString().slice(0, 10);
};

const subtractDays = (date, days) => {
  const copy = new Date(date);
  copy.setDate(copy.getDate() - days);
  return copy;
};

const clampRange = (start, end) => {
  const today = startOfToday();
  const clampedEnd = end > today ? today : end;
  let clampedStart = start > clampedEnd ? clampedEnd : start;
  const diff =
    (clampedEnd.getTime() - clampedStart.getTime()) / (1000 * 60 * 60 * 24);
  if (diff >= MAX_WINDOW_DAYS) {
    clampedStart = subtractDays(clampedEnd, MAX_WINDOW_DAYS - 1);
  }
  return {
    startDate: toISODate(clampedStart),
    endDate: toISODate(clampedEnd)
  };
};

const resolveDateRange = ({ preset, customStart, customEnd }) => {
  const today = startOfToday();
  if (preset === 'CUSTOM' && customStart && customEnd) {
    const start = new Date(`${customStart}T00:00:00Z`);
    const end = new Date(`${customEnd}T00:00:00Z`);
    return clampRange(start, end);
  }

  const matchedPreset = DATE_PRESETS.find((item) => item.value === preset);
  const days = matchedPreset?.days ?? 29;
  const start = subtractDays(today, days);
  return clampRange(start, today);
};

const determineGranularity = (range) => {
  const start = new Date(`${range.startDate}T00:00:00Z`);
  const end = new Date(`${range.endDate}T00:00:00Z`);
  const diffDays = Math.floor(
    (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
  );
  if (diffDays > 75) {
    return 'month';
  }
  if (diffDays > 35) {
    return 'week';
  }
  return 'day';
};

const getCustomerId = (customer) =>
  customer?.CustomerGUID ||
  customer?.customer_proxy_team_id ||
  customer?.team_id ||
  customer?.id ||
  null;

const getCustomerName = (customer) =>
  customer?.DisplayName ||
  customer?.customer_name ||
  customer?.name ||
  (getCustomerId(customer)
    ? `Customer ${getCustomerId(customer).slice(0, 8)}`
    : 'Unknown customer');

const getProductName = (product) =>
  product?.name || product?.productName || 'Unnamed product';

const slugify = (value) =>
  value
    ? value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
    : 'unknown';

const escapeCsv = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  const stringValue = String(value);
  if (/[\",\\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const downloadCsv = (rows, fileName) => {
  const blob = new Blob([rows.join('\\n')], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const copyToClipboard = async (value) => {
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(value);
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = value;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  } catch (error) {
    console.error('Failed to copy value to clipboard', error);
  }
};

function relativeTime(iso) {
  if (!iso) return '—';
  const then = new Date(iso);
  const now = new Date();
  const seconds = Math.floor((now - then) / 1000);
  if (Number.isNaN(seconds) || seconds < 0) return new Date(iso).toLocaleString();
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return then.toLocaleDateString();
}

// Categorical palette for the "split by key" chart — the current single-line
// green leads so a customer with one key that gains a second still sees a
// familiar color. Hash-based lookup (not array index) keeps a key's color
// stable across refetches regardless of response ordering.
const API_KEY_COLOR_PALETTE = [
  '#6EE7B7',
  '#93C5FD',
  '#FDE68A',
  '#FCA5A5',
  '#C4B5FD',
  '#5EEAD4',
  '#FDBA74',
  '#F9A8D4'
];

const colorForApiKey = (apiKeyId) => {
  let hash = 0;
  for (let i = 0; i < apiKeyId.length; i += 1) {
    hash = (hash * 31 + apiKeyId.charCodeAt(i)) | 0;
  }
  const index = Math.abs(hash) % API_KEY_COLOR_PALETTE.length;
  return API_KEY_COLOR_PALETTE[index];
};

export default function ConsumptionDashboard() {
  const { selectedTeam } = useContext(AppContext);
  // Deep-link target from Customer Overview (?customerId=...). Read once at mount
  // and seed selection synchronously so the picker reflects it on first paint.
  const [searchParams] = useSearchParams();
  const deepLinkCustomerId = searchParams.get('customerId') || '';
  const [activeTab, setActiveTab] = useState('customer');
  const [loadingReference, setLoadingReference] = useState(false);
  const [referenceError, setReferenceError] = useState('');

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(deepLinkCustomerId);
  const [selectedProductId, setSelectedProductId] = useState('');

  const [customerFilters, setCustomerFilters] = useState({
    preset: 'LAST_30_DAYS',
    customStart: '',
    customEnd: '',
    productId: null,
    apiKeyId: null
  });
  const [productFilters, setProductFilters] = useState({
    preset: 'LAST_30_DAYS',
    customStart: '',
    customEnd: '',
    customerId: null,
    apiKeyId: null
  });

  const [customerSummary, setCustomerSummary] = useState(null);
  const [customerSeries, setCustomerSeries] = useState([]);
  const [customerValidationSeries, setCustomerValidationSeries] = useState([]);
  const [customerEvents, setCustomerEvents] = useState([]);
  const [customerPagination, setCustomerPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 1
  });
  const [customerEventType, setCustomerEventType] = useState('usage');
  const [customerError, setCustomerError] = useState('');
  const [loadingCustomerOverview, setLoadingCustomerOverview] = useState(false);
  const [loadingCustomerEvents, setLoadingCustomerEvents] = useState(false);
  const [customerExporting, setCustomerExporting] = useState(false);
  const [splitCustomerByKey, setSplitCustomerByKey] = useState(false);
  const [customerSeriesByKey, setCustomerSeriesByKey] = useState([]);
  const [customerKeyLegend, setCustomerKeyLegend] = useState([]);
  const [customerLiveEvents, setCustomerLiveEvents] = useState([]);
  const [customerLiveUpdatedAt, setCustomerLiveUpdatedAt] = useState(null);

  const [productSummary, setProductSummary] = useState(null);
  const [productSeries, setProductSeries] = useState([]);
  const [productValidationSeries, setProductValidationSeries] = useState([]);
  const [productEvents, setProductEvents] = useState([]);
  const [productPagination, setProductPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 1
  });
  const [productEventType, setProductEventType] = useState('usage');
  const [productError, setProductError] = useState('');
  const [loadingProductOverview, setLoadingProductOverview] = useState(false);
  const [loadingProductEvents, setLoadingProductEvents] = useState(false);
  const [productExporting, setProductExporting] = useState(false);
  const [splitProductByKey, setSplitProductByKey] = useState(false);
  const [productSeriesByKey, setProductSeriesByKey] = useState([]);
  const [productKeyLegend, setProductKeyLegend] = useState([]);
  const [productLiveEvents, setProductLiveEvents] = useState([]);
  const [productLiveUpdatedAt, setProductLiveUpdatedAt] = useState(null);

  const customerEventsRequest = useRef(0);
  const productEventsRequest = useRef(0);
  const customerLiveSeenIds = useRef(new Set());
  const productLiveSeenIds = useRef(new Set());

  // Timestamp of the last successful overview load, surfaced as an "as of" stamp
  // so a left-open tab doesn't read as stale (we fetch once per selection, no polling).
  const [customerLastRefreshed, setCustomerLastRefreshed] = useState(null);
  const [productLastRefreshed, setProductLastRefreshed] = useState(null);

  const selectedCustomer = useMemo(
    () =>
      customers.find((customer) => getCustomerId(customer) === selectedCustomerId) ||
      null,
    [customers, selectedCustomerId]
  );

  const selectedProduct = useMemo(
    () => products.find((product) => product.id === selectedProductId) || null,
    [products, selectedProductId]
  );

  const customerDateRange = useMemo(
    () => resolveDateRange(customerFilters),
    [customerFilters]
  );

  const productDateRange = useMemo(
    () => resolveDateRange(productFilters),
    [productFilters]
  );

  const customerOptions = useMemo(
    () =>
      customers.map((customer) => ({
        value: getCustomerId(customer),
        label: getCustomerName(customer)
      })),
    [customers]
  );

  const productOptions = useMemo(
    () =>
      products.map((product) => ({
        value: product.id,
        label: `${getProductName(product)} · ${product.id}`
      })),
    [products]
  );

  const customerProductOptions = useMemo(() => {
    const source =
      customerEventType === 'validation'
        ? customerSummary?.validationTotals?.products
        : customerSummary?.totals?.products;
    const items = Array.isArray(source) ? source : [];
    const suffix = customerEventType === 'validation' ? 'validations' : 'units';
    return items.map((product) => ({
      value: product.scope_id,
      label: `${product.scope_name} (${formatNumber(product.total_units)} ${suffix})`
    }));
  }, [customerSummary, customerEventType]);

  const customerKeyOptions = useMemo(() => {
    const source =
      customerEventType === 'validation'
        ? customerSummary?.validationTotals?.apiKeys
        : customerSummary?.totals?.apiKeys;
    const keys = Array.isArray(source) ? source : [];
    const suffix = customerEventType === 'validation' ? 'validations' : 'units';
    return keys
      .filter((key) =>
        customerFilters.productId ? key.parent_id === customerFilters.productId : true
      )
      .map((key) => ({
        value: key.scope_id,
        label: `${key.scope_name} (${formatNumber(key.total_units)} ${suffix})`
      }));
  }, [customerSummary, customerFilters.productId, customerEventType]);

  const productCustomerOptions = useMemo(() => {
    const source =
      productEventType === 'validation'
        ? productSummary?.validationTotals?.customers
        : productSummary?.totals?.customers;
    const items = Array.isArray(source) ? source : [];
    const suffix = productEventType === 'validation' ? 'validations' : 'units';
    return items.map((customer) => ({
      value: customer.scope_id,
      label: `${customer.scope_name} (${formatNumber(customer.total_units)} ${suffix})`
    }));
  }, [productSummary, productEventType]);

  const productKeyOptions = useMemo(() => {
    const source =
      productEventType === 'validation'
        ? productSummary?.validationTotals?.apiKeys
        : productSummary?.totals?.apiKeys;
    const keys = Array.isArray(source) ? source : [];
    const suffix = productEventType === 'validation' ? 'validations' : 'units';
    return keys
      .filter((key) =>
        productFilters.customerId ? key.parent_id === productFilters.customerId : true
      )
      .map((key) => ({
        value: key.scope_id,
        label: `${key.scope_name} (${formatNumber(key.total_units)} ${suffix})`
      }));
  }, [productSummary, productFilters.customerId, productEventType]);

  const hasMoreCustomerEvents = customerEvents.length < customerPagination.total;
  const hasMoreProductEvents = productEvents.length < productPagination.total;

  useEffect(() => {
    const loadReferenceData = async () => {
      if (!selectedTeam?.teamId) {
        setCustomers([]);
        setProducts([]);
        return;
      }

      setLoadingReference(true);
      setReferenceError('');

      try {
        const authHeaders = authService.getAuthHeaders();
        const [customersResponse, productsResponse] = await Promise.all([
          fetch(
            `${config.apiBaseUrl}/api/customers?vendorTeamId=${selectedTeam.teamId}`,
            { headers: { ...authHeaders, 'Content-Type': 'application/json' } }
          ),
          fetch(
            `${config.apiBaseUrl}/api/products?teamId=${selectedTeam.teamId}`,
            { headers: { ...authHeaders, 'Content-Type': 'application/json' } }
          )
        ]);

        if (!customersResponse.ok) {
          const body = await customersResponse.json();
          throw new Error(body?.error || 'Failed to load customers');
        }

        if (!productsResponse.ok) {
          const body = await productsResponse.json();
          throw new Error(body?.error || 'Failed to load products');
        }

        const customersBody = await customersResponse.json();
        const productsBody = await productsResponse.json();

        setCustomers(
          Array.isArray(customersBody.customers)
            ? customersBody.customers
            : Array.isArray(customersBody)
            ? customersBody
            : []
        );

        const productList = Array.isArray(productsBody.products)
          ? productsBody.products
          : productsBody.success && Array.isArray(productsBody.products)
          ? productsBody.products
          : [];
        setProducts(productList);
      } catch (error) {
        console.error('Error loading reference data:', error);
        setReferenceError(error.message || 'Failed to load team data');
        setCustomers([]);
        setProducts([]);
      } finally {
        setLoadingReference(false);
      }
    };

    loadReferenceData();
  }, [selectedTeam]);

  // Clear selections when the user switches teams. Skips the initial mount so a
  // deep-linked customer (seeded from ?customerId=) survives first render — only an
  // actual team change wipes it. Tracking the previous team id keeps this StrictMode-safe.
  const prevTeamIdRef = useRef(selectedTeam?.teamId);
  useEffect(() => {
    const teamId = selectedTeam?.teamId;
    if (prevTeamIdRef.current === teamId) return;
    prevTeamIdRef.current = teamId;
    setSelectedCustomerId('');
    setSelectedProductId('');
    setCustomerSummary(null);
    setCustomerValidationSeries([]);
    setProductSummary(null);
    setProductValidationSeries([]);
    setCustomerEvents([]);
    setProductEvents([]);
    setCustomerEventType('usage');
    setProductEventType('usage');
    setCustomerPagination({
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      total: 0,
      totalPages: 1
    });
    setProductPagination({
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      total: 0,
      totalPages: 1
    });
    setCustomerFilters((prev) => ({
      ...prev,
      productId: null,
      apiKeyId: null
    }));
    setProductFilters((prev) => ({
      ...prev,
      customerId: null,
      apiKeyId: null
    }));
  }, [selectedTeam]);

  const loadCustomerOverview = useCallback(async () => {
    if (!selectedCustomerId) {
      setCustomerSummary(null);
      setCustomerSeries([]);
      setCustomerValidationSeries([]);
      setCustomerSeriesByKey([]);
      setCustomerKeyLegend([]);
      return;
    }

    setLoadingCustomerOverview(true);
    setCustomerError('');

    try {
      const paramsBase = {
        startDate: customerDateRange.startDate,
        endDate: customerDateRange.endDate
      };
      if (customerFilters.productId) {
        paramsBase.productId = customerFilters.productId;
      }
      if (customerFilters.apiKeyId) {
        paramsBase.apiKeyId = customerFilters.apiKeyId;
      }
      const granularity = determineGranularity(customerDateRange);
      const [summaryResponse, seriesResponse] = await Promise.all([
        reportingService.getCustomerSummary(selectedCustomerId, paramsBase),
        reportingService.getCustomerSeries(selectedCustomerId, {
          ...paramsBase,
          granularity,
          splitByApiKey: splitCustomerByKey || undefined
        })
      ]);

      setCustomerSummary(summaryResponse);
      const chartData = (seriesResponse?.series || []).map((point) => ({
        date: point.bucket,
        units: point.totalUnits
      }));
      setCustomerSeries(chartData);
      const validationChart = (seriesResponse?.validationSeries || []).map((point) => ({
        date: point.bucket,
        validations: point.totalValidations
      }));
      setCustomerValidationSeries(validationChart);
      setCustomerSeriesByKey(seriesResponse?.seriesByApiKey || []);
      setCustomerKeyLegend(seriesResponse?.apiKeys || []);
      setCustomerLastRefreshed(new Date());
    } catch (error) {
      console.error('Error loading customer overview:', error);
      setCustomerError(error.message || 'Failed to load customer reporting');
      setCustomerSummary(null);
      setCustomerSeries([]);
      setCustomerValidationSeries([]);
      setCustomerSeriesByKey([]);
      setCustomerKeyLegend([]);
    } finally {
      setLoadingCustomerOverview(false);
    }
  }, [
    selectedCustomerId,
    customerDateRange,
    customerFilters.productId,
    customerFilters.apiKeyId,
    splitCustomerByKey
  ]);

  const loadCustomerEvents = useCallback(
    async (page = 1, append = false) => {
      if (!selectedCustomerId || customerEventType === 'live') {
        setCustomerEvents([]);
        setCustomerPagination({
          page: 1,
          pageSize: DEFAULT_PAGE_SIZE,
          total: 0,
          totalPages: 1
        });
        return;
      }

      setLoadingCustomerEvents(true);
      setCustomerError('');
      const requestId = ++customerEventsRequest.current;

      try {
        const params = {
          startDate: customerDateRange.startDate,
          endDate: customerDateRange.endDate,
          page,
          pageSize: DEFAULT_PAGE_SIZE,
          eventType: customerEventType
        };
        if (customerFilters.productId) {
          params.productId = customerFilters.productId;
        }
        if (customerFilters.apiKeyId) {
          params.apiKeyId = customerFilters.apiKeyId;
        }

        const response = await reportingService.getCustomerEvents(
          selectedCustomerId,
          params
        );

        if (requestId !== customerEventsRequest.current) {
          return;
        }

        const incoming = response.events || [];
        setCustomerEvents((previous) =>
          append ? [...previous, ...incoming] : incoming
        );
        setCustomerPagination((previous) => {
          if (response.pagination) {
            return response.pagination;
          }
          const previousTotal = append ? previous.total || previous.page * previous.pageSize : 0;
          const total = previousTotal + incoming.length;
          const totalPages = Math.max(1, Math.ceil(total / DEFAULT_PAGE_SIZE));
          return {
            page: params.page,
            pageSize: DEFAULT_PAGE_SIZE,
            total,
            totalPages
          };
        });
      } catch (error) {
        console.error('Error loading customer events:', error);
        if (!append) {
          setCustomerEvents([]);
        }
        setCustomerError(error.message || 'Failed to load activity');
      } finally {
        if (requestId === customerEventsRequest.current) {
          setLoadingCustomerEvents(false);
        }
      }
    },
    [
      selectedCustomerId,
      customerDateRange,
      customerFilters.productId,
      customerFilters.apiKeyId,
      customerEventType
    ]
  );

  useEffect(() => {
    setCustomerEvents([]);
    setCustomerPagination({
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      total: 0,
      totalPages: 1
    });
  }, [customerEventType, selectedCustomerId]);

  useEffect(() => {
    setCustomerFilters((prev) => ({
      ...prev,
      productId: null,
      apiKeyId: null
    }));
  }, [customerEventType]);

  // Recent-activity "Live" view: the codebase deliberately avoids ambient
  // polling elsewhere (see customerLastRefreshed above), so this stays
  // strictly opt-in — only active while the "Live" toggle is selected, and
  // paused via the Page Visibility API when the tab isn't in the foreground.
  useEffect(() => {
    customerLiveSeenIds.current = new Set();
    setCustomerLiveEvents([]);
    setCustomerLiveUpdatedAt(null);

    if (!selectedCustomerId || customerEventType !== 'live') {
      return undefined;
    }

    let cancelled = false;
    let intervalId = null;

    const poll = async () => {
      try {
        const response = await reportingService.getCustomerRecentActivity(
          selectedCustomerId,
          { limit: 50 }
        );
        if (cancelled) return;
        const incoming = response.events || [];
        const fresh = incoming.filter(
          (event) => !customerLiveSeenIds.current.has(event.id)
        );
        if (fresh.length > 0) {
          fresh.forEach((event) => customerLiveSeenIds.current.add(event.id));
          setCustomerLiveEvents((previous) => [...fresh, ...previous].slice(0, 100));
        }
        setCustomerLiveUpdatedAt(new Date());
      } catch (error) {
        console.error('Error polling recent activity:', error);
      }
    };

    const startInterval = () => {
      if (intervalId) return;
      intervalId = setInterval(poll, 4000);
    };
    const stopInterval = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    poll();
    if (document.visibilityState === 'visible') {
      startInterval();
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        poll();
        startInterval();
      } else {
        stopInterval();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [selectedCustomerId, customerEventType]);

  const loadProductOverview = useCallback(async () => {
    if (!selectedProductId) {
      setProductSummary(null);
      setProductSeries([]);
      setProductValidationSeries([]);
      setProductSeriesByKey([]);
      setProductKeyLegend([]);
      return;
    }

    setLoadingProductOverview(true);
    setProductError('');

    try {
      const paramsBase = {
        startDate: productDateRange.startDate,
        endDate: productDateRange.endDate
      };
      if (productFilters.customerId) {
        paramsBase.customerId = productFilters.customerId;
      }
      if (productFilters.apiKeyId) {
        paramsBase.apiKeyId = productFilters.apiKeyId;
      }
      const granularity = determineGranularity(productDateRange);
      const [summaryResponse, seriesResponse] = await Promise.all([
        reportingService.getProductSummary(selectedProductId, paramsBase),
        reportingService.getProductSeries(selectedProductId, {
          ...paramsBase,
          granularity,
          splitByApiKey: splitProductByKey || undefined
        })
      ]);

      setProductSummary(summaryResponse);
      const chartData = (seriesResponse?.series || []).map((point) => ({
        date: point.bucket,
        units: point.totalUnits
      }));
      setProductSeries(chartData);
      const validationChart = (seriesResponse?.validationSeries || []).map((point) => ({
        date: point.bucket,
        validations: point.totalValidations
      }));
      setProductValidationSeries(validationChart);
      setProductSeriesByKey(seriesResponse?.seriesByApiKey || []);
      setProductKeyLegend(seriesResponse?.apiKeys || []);
      setProductLastRefreshed(new Date());
    } catch (error) {
      console.error('Error loading product overview:', error);
      setProductError(error.message || 'Failed to load product reporting');
      setProductSummary(null);
      setProductSeries([]);
      setProductValidationSeries([]);
      setProductSeriesByKey([]);
      setProductKeyLegend([]);
    } finally {
      setLoadingProductOverview(false);
    }
  }, [
    selectedProductId,
    productDateRange,
    productFilters.customerId,
    productFilters.apiKeyId,
    splitProductByKey
  ]);

const loadProductEvents = useCallback(
    async (page = 1, append = false) => {
      if (!selectedProductId || productEventType === 'live') {
        setProductEvents([]);
        setProductPagination({
          page: 1,
          pageSize: DEFAULT_PAGE_SIZE,
          total: 0,
          totalPages: 1
        });
        return;
      }

      setLoadingProductEvents(true);
      setProductError('');
      const requestId = ++productEventsRequest.current;

      try {
        const params = {
          startDate: productDateRange.startDate,
          endDate: productDateRange.endDate,
          page,
          pageSize: DEFAULT_PAGE_SIZE,
          eventType: productEventType
        };
        if (productFilters.customerId) {
          params.customerId = productFilters.customerId;
        }
        if (productFilters.apiKeyId) {
          params.apiKeyId = productFilters.apiKeyId;
        }

        const response = await reportingService.getProductEvents(
          selectedProductId,
          params
        );

        if (requestId !== productEventsRequest.current) {
          return;
        }

        const incoming = response.events || [];
        setProductEvents((previous) =>
          append ? [...previous, ...incoming] : incoming
        );
        setProductPagination((previous) => {
          if (response.pagination) {
            return response.pagination;
          }
          const previousTotal = append ? previous.total || previous.page * previous.pageSize : 0;
          const total = previousTotal + incoming.length;
          const totalPages = Math.max(1, Math.ceil(total / DEFAULT_PAGE_SIZE));
          return {
            page: params.page,
            pageSize: DEFAULT_PAGE_SIZE,
            total,
            totalPages
          };
        });
      } catch (error) {
        console.error('Error loading product events:', error);
        if (!append) {
          setProductEvents([]);
        }
        setProductError(error.message || 'Failed to load activity');
      } finally {
        if (requestId === productEventsRequest.current) {
          setLoadingProductEvents(false);
        }
      }
    },
    [selectedProductId, productDateRange, productFilters.customerId, productFilters.apiKeyId, productEventType]
  );

  useEffect(() => {
    setProductEvents([]);
    setProductPagination({
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      total: 0,
      totalPages: 1
    });
  }, [productEventType, selectedProductId]);

  useEffect(() => {
    setProductFilters((prev) => ({
      ...prev,
      customerId: null,
      apiKeyId: null
    }));
  }, [productEventType]);

  useEffect(() => {
    productLiveSeenIds.current = new Set();
    setProductLiveEvents([]);
    setProductLiveUpdatedAt(null);

    if (!selectedProductId || productEventType !== 'live') {
      return undefined;
    }

    let cancelled = false;
    let intervalId = null;

    const poll = async () => {
      try {
        const response = await reportingService.getProductRecentActivity(
          selectedProductId,
          { limit: 50 }
        );
        if (cancelled) return;
        const incoming = response.events || [];
        const fresh = incoming.filter(
          (event) => !productLiveSeenIds.current.has(event.id)
        );
        if (fresh.length > 0) {
          fresh.forEach((event) => productLiveSeenIds.current.add(event.id));
          setProductLiveEvents((previous) => [...fresh, ...previous].slice(0, 100));
        }
        setProductLiveUpdatedAt(new Date());
      } catch (error) {
        console.error('Error polling recent activity:', error);
      }
    };

    const startInterval = () => {
      if (intervalId) return;
      intervalId = setInterval(poll, 4000);
    };
    const stopInterval = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    poll();
    if (document.visibilityState === 'visible') {
      startInterval();
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        poll();
        startInterval();
      } else {
        stopInterval();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [selectedProductId, productEventType]);

  useEffect(() => {
    loadCustomerOverview();
    loadCustomerEvents(1, false);
  }, [selectedCustomerId, customerEventType, loadCustomerOverview, loadCustomerEvents]);

  useEffect(() => {
    loadProductOverview();
    loadProductEvents(1, false);
  }, [selectedProductId, productEventType, loadProductOverview, loadProductEvents]);

  const handleCustomerPresetChange = (option) => {
    setCustomerFilters((prev) => ({
      ...prev,
      preset: option?.value || 'LAST_30_DAYS'
    }));
  };

  const handleProductPresetChange = (option) => {
    setProductFilters((prev) => ({
      ...prev,
      preset: option?.value || 'LAST_30_DAYS'
    }));
  };

  const handleCustomerProductChange = (option) => {
    setCustomerFilters((prev) => ({
      ...prev,
      productId: option?.value || null,
      apiKeyId: null
    }));
  };

  const handleCustomerKeyChange = (option) => {
    setCustomerFilters((prev) => ({
      ...prev,
      apiKeyId: option?.value || null
    }));
  };

  const handleProductCustomerChange = (option) => {
    setProductFilters((prev) => ({
      ...prev,
      customerId: option?.value || null,
      apiKeyId: null
    }));
  };

  const handleProductKeyChange = (option) => {
    setProductFilters((prev) => ({
      ...prev,
      apiKeyId: option?.value || null
    }));
  };

  const handleCustomerLoadMore = () => {
    if (hasMoreCustomerEvents && !loadingCustomerEvents) {
      loadCustomerEvents(customerPagination.page + 1, true);
    }
  };

  const handleProductLoadMore = () => {
    if (hasMoreProductEvents && !loadingProductEvents) {
      loadProductEvents(productPagination.page + 1, true);
    }
  };

  const handleCustomerExport = useCallback(async () => {
    if (!selectedCustomerId || customerEventType !== 'usage') {
      return;
    }
    setCustomerExporting(true);
    setCustomerError('');

    try {
      const rows = [];
      const params = {
        startDate: customerDateRange.startDate,
        endDate: customerDateRange.endDate,
        pageSize: 500,
        eventType: 'usage'
      };
      if (customerFilters.productId) {
        params.productId = customerFilters.productId;
      }
      if (customerFilters.apiKeyId) {
        params.apiKeyId = customerFilters.apiKeyId;
      }

      let page = 1;
      let fetched = 0;
      let total = 0;

      do {
        const response = await reportingService.getCustomerEvents(
          selectedCustomerId,
          { ...params, page }
        );
        const events = response.events || [];
        if (events.length === 0) {
          break;
        }
        rows.push(
          ...events.map((event) =>
            [
              escapeCsv(event.createdAt),
              escapeCsv(event.usageDate),
              escapeCsv(event.units),
              escapeCsv(event.product?.name),
              escapeCsv(event.apiKey?.nickname || event.apiKey?.id),
              escapeCsv(event.apiKey?.id),
              escapeCsv(event.customerName),
              escapeCsv(event.customerOrganizationName),
              escapeCsv(event.vendorOrganizationName)
            ].join(',')
          )
        );
        fetched += events.length;
        total = response.pagination?.total ?? fetched;
        page += 1;
        if (page > (response.pagination?.totalPages || page)) {
          break;
        }
      } while (fetched < total);

      const header = [
        'Recorded At',
        'Usage Date',
        'Units',
        'Product',
        'API Key Nickname',
        'API Key ID',
        'Customer Name',
        'Customer Org',
        'Vendor Org'
      ].join(',');

      const fileName = `customer-activity_${slugify(
        customerSummary?.customer?.name || getCustomerName(selectedCustomer)
      )}_${customerDateRange.startDate}_${customerDateRange.endDate}.csv`;

      downloadCsv([header, ...rows], fileName);
    } catch (error) {
      console.error('Error exporting customer events:', error);
      setCustomerError(error.message || 'Failed to export CSV');
    } finally {
      setCustomerExporting(false);
    }
  }, [
    selectedCustomerId,
    customerFilters.productId,
    customerFilters.apiKeyId,
    customerDateRange,
    customerSummary,
    selectedCustomer,
    customerEventType
  ]);

  const handleProductExport = useCallback(async () => {
    if (!selectedProductId || productEventType !== 'usage') {
      return;
    }
    setProductExporting(true);
    setProductError('');

    try {
      const rows = [];
      const params = {
        startDate: productDateRange.startDate,
        endDate: productDateRange.endDate,
        pageSize: 500,
        eventType: 'usage'
      };
      if (productFilters.customerId) {
        params.customerId = productFilters.customerId;
      }
      if (productFilters.apiKeyId) {
        params.apiKeyId = productFilters.apiKeyId;
      }

      let page = 1;
      let fetched = 0;
      let total = 0;

      do {
        const response = await reportingService.getProductEvents(
          selectedProductId,
          { ...params, page }
        );
        const events = response.events || [];
        if (events.length === 0) {
          break;
        }
        rows.push(
          ...events.map((event) =>
            [
              escapeCsv(event.createdAt),
              escapeCsv(event.usageDate),
              escapeCsv(event.units),
              escapeCsv(event.customer?.teamName),
              escapeCsv(event.customer?.organizationName),
              escapeCsv(event.apiKey?.nickname || event.apiKey?.id),
              escapeCsv(event.apiKey?.id),
              escapeCsv(event.plan?.name)
            ].join(',')
          )
        );
        fetched += events.length;
        total = response.pagination?.total ?? fetched;
        page += 1;
        if (page > (response.pagination?.totalPages || page)) {
          break;
        }
      } while (fetched < total);

      const header = [
        'Recorded At',
        'Usage Date',
        'Units',
        'Customer Team',
        'Customer Org',
        'API Key Nickname',
        'API Key ID',
        'Plan'
      ].join(',');

      const fileName = `product-activity_${slugify(
        getProductName(selectedProduct)
      )}_${productDateRange.startDate}_${productDateRange.endDate}.csv`;

      downloadCsv([header, ...rows], fileName);
    } catch (error) {
      console.error('Error exporting product events:', error);
      setProductError(error.message || 'Failed to export CSV');
    } finally {
      setProductExporting(false);
    }
  }, [
    selectedProductId,
    productFilters.customerId,
    productFilters.apiKeyId,
    productDateRange,
    selectedProduct,
    productEventType
  ]);

  if (!selectedTeam) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-slate-100">Reporting</h1>
        <p className="text-slate-400">
          Select a team to view customer and product reporting.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-100">Reporting</h1>
          <p className="text-slate-500 text-sm">
            Monitor customer and product activity across the last 90 days.{' '}
            <a href="/docs/" className="text-indigo-400 hover:text-indigo-300">
              View docs →
            </a>
          </p>
          <Link
            to="/customer-overview"
            className="inline-block mt-1 text-sm text-indigo-400 hover:text-indigo-300"
          >
            ← Back to customer overview
          </Link>
        </div>
        <div className="inline-flex rounded-md bg-slate-800 p-1">
          <button
            type="button"
            className={`px-4 py-2 rounded text-sm transition ${
              activeTab === 'customer'
                ? 'bg-indigo-600 text-white font-medium'
                : 'text-slate-400 hover:text-slate-200'
            }`}
            onClick={() => setActiveTab('customer')}
          >
            Customer view
          </button>
          <button
            type="button"
            className={`px-4 py-2 rounded text-sm transition ${
              activeTab === 'product'
                ? 'bg-indigo-600 text-white font-medium'
                : 'text-slate-400 hover:text-slate-200'
            }`}
            onClick={() => setActiveTab('product')}
          >
            Product view
          </button>
        </div>
      </header>

        {referenceError && (
          <div className="bg-red-500/20 border border-red-400/40 text-red-100 px-4 py-3 rounded">
            {referenceError}
          </div>
        )}

        {loadingReference && (
          <div className="bg-slate-900 border border-slate-700 rounded-lg px-4 py-6 text-center text-slate-300">
            Loading customers & products...
          </div>
        )}

        {!loadingReference && activeTab === 'customer' && (
          <section className="space-y-6">
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 shadow-lg space-y-4">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    Customer
                  </label>
                  <Select
                    options={customerOptions}
                    value={
                      selectedCustomerId
                        ? customerOptions.find(
                            (option) => option.value === selectedCustomerId
                          )
                        : null
                    }
                    onChange={(option) => {
                      setSelectedCustomerId(option?.value || '');
                      setCustomerFilters((prev) => ({
                        ...prev,
                        productId: null,
                        apiKeyId: null
                      }));
                    }}
                    classNamePrefix="react-select"
                    styles={selectStyles}
                    isClearable
                    placeholder="Select a customer..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    Date range
                  </label>
                  <Select
                    options={DATE_PRESETS}
                    value={DATE_PRESETS.find(
                      (option) => option.value === customerFilters.preset
                    )}
                    classNamePrefix="react-select"
                    styles={selectStyles}
                    onChange={handleCustomerPresetChange}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">
                      Start
                    </label>
                    <input
                      type="date"
                      className="w-full rounded bg-slate-900 border border-slate-700 text-slate-100 px-3 py-2"
                      value={customerFilters.customStart}
                      onChange={(event) =>
                        setCustomerFilters((prev) => ({
                          ...prev,
                          customStart: event.target.value
                        }))
                      }
                      disabled={customerFilters.preset !== 'CUSTOM'}
                      max={customerDateRange.endDate}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">
                      End
                    </label>
                    <input
                      type="date"
                      className="w-full rounded bg-slate-900 border border-slate-700 text-slate-100 px-3 py-2"
                      value={customerFilters.customEnd}
                      onChange={(event) =>
                        setCustomerFilters((prev) => ({
                          ...prev,
                          customEnd: event.target.value
                        }))
                      }
                      disabled={customerFilters.preset !== 'CUSTOM'}
                      max={toISODate(startOfToday())}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    Product filter
                  </label>
                  <Select
                    options={customerProductOptions}
                    value={
                      customerFilters.productId
                        ? customerProductOptions.find(
                            (option) => option.value === customerFilters.productId
                          )
                        : null
                    }
                    classNamePrefix="react-select"
                    styles={selectStyles}
                    onChange={handleCustomerProductChange}
                    isClearable
                    placeholder="All products"
                    isDisabled={!selectedCustomerId}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    API key filter
                  </label>
                  <Select
                    options={customerKeyOptions}
                    value={
                      customerFilters.apiKeyId
                        ? customerKeyOptions.find(
                            (option) => option.value === customerFilters.apiKeyId
                          )
                        : null
                    }
                    classNamePrefix="react-select"
                    styles={selectStyles}
                    onChange={handleCustomerKeyChange}
                    isClearable
                    placeholder="All keys"
                    isDisabled={!selectedCustomerId}
                  />
                </div>
              </div>
              <div className="text-sm text-slate-500">
                Reporting window: {customerDateRange.startDate} to{' '}
                {customerDateRange.endDate}
              </div>
            </div>

            {customerError && (
              <div className="bg-red-500/20 border border-red-400/40 text-red-100 px-4 py-3 rounded">
                {customerError}
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-4">
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                <p className="text-sm text-slate-500">Total units</p>
                <p className="text-3xl font-semibold">
                  {formatNumber(customerSummary?.totals?.totalUnits || 0)}
                </p>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                <p className="text-sm text-slate-500">Total validations</p>
                <p className="text-3xl font-semibold">
                  {formatNumber(customerSummary?.validationTotals?.totalValidations || 0)}
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  {formatNumber(customerSummary?.validationTotals?.validCount || 0)} valid ·{' '}
                  {formatNumber(customerSummary?.validationTotals?.invalidCount || 0)} invalid
                </p>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                <p className="text-sm text-slate-500">Top product</p>
                <p className="text-lg font-semibold">
                  {customerSummary?.totals?.products?.[0]
                    ? `${customerSummary.totals.products[0].scope_name} · ${formatNumber(
                        customerSummary.totals.products[0].total_units
                      )} units`
                    : '-'}
                </p>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                <p className="text-sm text-slate-500">Top API key</p>
                <p className="text-lg font-semibold">
                  {customerSummary?.totals?.apiKeys?.[0]
                    ? `${customerSummary.totals.apiKeys[0].scope_name} · ${formatNumber(
                        customerSummary.totals.apiKeys[0].total_units
                      )} units`
                    : '-'}
                </p>
              </div>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 space-y-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <h2 className="text-xl font-semibold">Usage trend</h2>
                <div className="flex items-center gap-3">
                  {customerLastRefreshed && (
                    <span
                      className="text-xs text-slate-500"
                      title={customerLastRefreshed.toLocaleString()}
                    >
                      as of{' '}
                      {customerLastRefreshed.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  )}
                  <button
                    type="button"
                    className="bg-slate-700 hover:bg-slate-600 text-slate-200 px-4 py-2 rounded-md transition disabled:opacity-50"
                    onClick={() => {
                      loadCustomerOverview();
                      loadCustomerEvents(1, false);
                    }}
                    disabled={loadingCustomerOverview || loadingCustomerEvents}
                  >
                    Refresh
                  </button>
                  <button
                    type="button"
                    className="bg-indigo-600 text-white font-medium px-4 py-2 rounded-md transition hover:bg-indigo-500 disabled:opacity-50"
                    onClick={handleCustomerExport}
                    disabled={
                      customerEventType !== 'usage' ||
                      !selectedCustomerId ||
                      customerEvents.length === 0 ||
                      customerExporting
                    }
                  >
                    {customerExporting ? 'Exporting...' : 'Export CSV'}
                  </button>
                </div>
              </div>
              <div className="space-y-6">
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-slate-300">Usage</h3>
                    {(customerSummary?.totals?.apiKeys?.length || 0) > 1 && (
                      <div className="inline-flex rounded-md bg-slate-800 p-1 self-start">
                        <button
                          type="button"
                          className={`px-3 py-1 rounded-md transition text-sm ${
                            !splitCustomerByKey
                              ? 'bg-indigo-600 text-white font-medium'
                              : 'text-slate-400 hover:text-slate-100'
                          }`}
                          onClick={() => setSplitCustomerByKey(false)}
                        >
                          Combined
                        </button>
                        <button
                          type="button"
                          className={`px-3 py-1 rounded-md transition text-sm ${
                            splitCustomerByKey
                              ? 'bg-indigo-600 text-white font-medium'
                              : 'text-slate-400 hover:text-slate-100'
                          }`}
                          onClick={() => setSplitCustomerByKey(true)}
                        >
                          Split by key
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="h-64">
                    {loadingCustomerOverview ? (
                      <div className="h-full flex items-center justify-center text-slate-400">
                        Loading chart...
                      </div>
                    ) : splitCustomerByKey && customerSeriesByKey.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={customerSeriesByKey}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
                          <XAxis dataKey="bucket" stroke="#ffffff80" />
                          <YAxis stroke="#ffffff80" />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#1f2240',
                              borderColor: '#ffffff40'
                            }}
                          />
                          <Legend />
                          {customerKeyLegend.map((key) => (
                            <Line
                              key={key.id}
                              type="monotone"
                              dataKey={key.id}
                              name={key.nickname}
                              stroke={colorForApiKey(key.id)}
                              strokeWidth={2}
                              dot={false}
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    ) : customerSeries.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={customerSeries}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
                          <XAxis dataKey="date" stroke="#ffffff80" />
                          <YAxis stroke="#ffffff80" />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#1f2240',
                              borderColor: '#ffffff40'
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="units"
                            stroke="#6EE7B7"
                            strokeWidth={3}
                            dot={{ r: 4 }}
                            name="Units"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-slate-500">
                        No usage recorded for this period.
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-300 mb-1">Validations</h3>
                  <p className="text-xs text-amber-400/80 mb-2">
                    Validation records are retained for ~24 hours — longer date ranges still
                    show only the most recent day.
                  </p>
                  <div className="h-64">
                    {loadingCustomerOverview ? (
                      <div className="h-full flex items-center justify-center text-slate-400">
                        Loading chart...
                      </div>
                    ) : customerValidationSeries.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={customerValidationSeries}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
                          <XAxis dataKey="date" stroke="#ffffff80" />
                          <YAxis stroke="#ffffff80" />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#1f2240',
                              borderColor: '#ffffff40'
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="validations"
                            stroke="#FDE68A"
                            strokeWidth={3}
                            dot={{ r: 4 }}
                            name="Validations"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-slate-500">
                        No validations recorded for this period.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 space-y-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <h2 className="text-xl font-semibold">Recent activity</h2>
                  <div className="inline-flex rounded-md bg-slate-800 p-1">
                    <button
                      type="button"
                      className={`px-3 py-1 rounded-md transition text-sm ${
                        customerEventType === 'usage'
                          ? 'bg-indigo-600 text-white font-medium'
                          : 'text-slate-400 hover:text-slate-100'
                      }`}
                      onClick={() => setCustomerEventType('usage')}
                    >
                      Usage
                    </button>
                    <button
                      type="button"
                      className={`px-3 py-1 rounded-md transition text-sm ${
                        customerEventType === 'validation'
                          ? 'bg-indigo-600 text-white font-medium'
                          : 'text-slate-400 hover:text-slate-100'
                      }`}
                      onClick={() => setCustomerEventType('validation')}
                    >
                      Validations
                    </button>
                    <button
                      type="button"
                      className={`px-3 py-1 rounded-md transition text-sm ${
                        customerEventType === 'live'
                          ? 'bg-indigo-600 text-white font-medium'
                          : 'text-slate-400 hover:text-slate-100'
                      }`}
                      onClick={() => setCustomerEventType('live')}
                      title="Per-call events, held for 48h — useful for confirming a hit just landed."
                    >
                      Live
                    </button>
                  </div>
                </div>
                <span className="text-sm text-slate-500">
                  {customerEventType === 'live' ? (
                    <>
                      {customerLiveEvents.length} recent{' '}
                      {customerLiveEvents.length === 1 ? 'event' : 'events'}
                      {customerLiveUpdatedAt && (
                        <> · updated {relativeTime(customerLiveUpdatedAt.toISOString())}</>
                      )}
                    </>
                  ) : (
                    <>
                      Showing {customerEvents.length} of {customerPagination.total}{' '}
                      {customerEventType === 'validation' ? 'validation' : 'usage'} events
                    </>
                  )}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left">
                  <thead>
                    {customerEventType === 'usage' ? (
                      <tr className="text-sm uppercase text-slate-500 border-b border-slate-700">
                        <th
                          className="py-3 pr-4"
                          title="Usage is aggregated into one row per key per UTC day; 'Recorded at' is the last time that day's row was updated, not a per-call timestamp."
                        >
                          Recorded at (UTC)
                        </th>
                        <th className="py-3 pr-4">Usage date (UTC)</th>
                        <th className="py-3 pr-4">Units</th>
                        <th className="py-3 pr-4">Product</th>
                        <th className="py-3 pr-4">API key</th>
                        <th className="py-3 pr-4">Plan</th>
                        <th className="py-3 pr-4">Customer org</th>
                      </tr>
                    ) : customerEventType === 'validation' ? (
                      <tr className="text-sm uppercase text-slate-500 border-b border-slate-700">
                        <th className="py-3 pr-4">Validated at</th>
                        <th className="py-3 pr-4">Result</th>
                        <th className="py-3 pr-4">API key</th>
                        <th className="py-3 pr-4">Product</th>
                        <th className="py-3 pr-4">Plan</th>
                        <th className="py-3 pr-4">Source</th>
                        <th className="py-3 pr-4">Customer org</th>
                      </tr>
                    ) : (
                      <tr className="text-sm uppercase text-slate-500 border-b border-slate-700">
                        <th
                          className="py-3 pr-4"
                          title="Real per-call timestamps, unlike the aggregated Usage tab."
                        >
                          Occurred
                        </th>
                        <th className="py-3 pr-4">API key</th>
                        <th className="py-3 pr-4">Units</th>
                      </tr>
                    )}
                  </thead>
                  <tbody className="divide-y divide-white/10 text-sm">
                    {customerEventType === 'usage'
                      ? customerEvents.map((event) => (
                          <tr key={event.id} className="hover:bg-slate-800">
                            <td className="py-3 pr-4 whitespace-nowrap text-slate-300">
                              {event.createdAt
                                ? new Date(event.createdAt).toLocaleString()
                                : '-'}
                            </td>
                            <td className="py-3 pr-4 text-slate-400">{event.usageDate}</td>
                            <td className="py-3 pr-4 text-slate-100">
                              {formatNumber(event.units)}
                            </td>
                            <td className="py-3 pr-4 text-slate-300">
                              {event.product?.name || '-'}
                            </td>
                            <td className="py-3 pr-4 text-slate-400">
                              {event.apiKey?.nickname || event.apiKey?.id || '-'}
                            </td>
                            <td className="py-3 pr-4 text-slate-400">
                              {event.plan?.name || '-'}
                            </td>
                            <td className="py-3 pr-4 text-slate-500">
                              {event.customerOrganizationName || '-'}
                            </td>
                          </tr>
                        ))
                      : customerEventType === 'validation'
                      ? customerEvents.map((event) => (
                          <tr key={event.id} className="hover:bg-slate-800">
                            <td className="py-3 pr-4 whitespace-nowrap text-slate-300">
                              {event.validatedAt
                                ? new Date(event.validatedAt).toLocaleString()
                                : '-'}
                            </td>
                            <td className="py-3 pr-4 text-slate-100">
                              {event.isValid ? 'Valid' : 'Rejected'}
                            </td>
                            <td className="py-3 pr-4 text-slate-400">
                              {event.apiKey?.nickname || event.apiKey?.id || '-'}
                            </td>
                            <td className="py-3 pr-4 text-slate-300">
                              {event.product?.name || '-'}
                            </td>
                            <td className="py-3 pr-4 text-slate-400">
                              {event.plan?.name || '-'}
                            </td>
                            <td className="py-3 pr-4 text-slate-400">
                              {event.source || '-'}
                            </td>
                            <td className="py-3 pr-4 text-slate-500">
                              {event.customer?.organizationName || '-'}
                            </td>
                          </tr>
                        ))
                      : customerLiveEvents.map((event) => (
                          <tr key={event.id} className="hover:bg-slate-800">
                            <td className="py-3 pr-4 whitespace-nowrap text-slate-300">
                              {relativeTime(event.occurredAt)}
                            </td>
                            <td className="py-3 pr-4 text-slate-400">
                              {event.apiKeyId}
                            </td>
                            <td className="py-3 pr-4 text-slate-100">
                              {formatNumber(event.units)}
                            </td>
                          </tr>
                        ))}
                    {customerEventType !== 'live' &&
                      customerEvents.length === 0 &&
                      !loadingCustomerEvents && (
                        <tr>
                          <td
                            colSpan={7}
                            className="py-6 text-center text-slate-500"
                          >
                            No {customerEventType === 'validation' ? 'validation' : 'usage'} events recorded for this selection.
                          </td>
                        </tr>
                      )}
                    {customerEventType === 'live' && customerLiveEvents.length === 0 && (
                      <tr>
                        <td colSpan={3} className="py-6 text-center text-slate-500">
                          Waiting for activity — events appear here within a few seconds of the call.
                        </td>
                      </tr>
                    )}
                    {customerEventType !== 'live' && loadingCustomerEvents && (
                      <tr>
                        <td
                          colSpan={7}
                          className="py-6 text-center text-slate-500"
                        >
                          Loading events...
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {customerEventType !== 'live' && hasMoreCustomerEvents && (
                <div className="flex justify-center">
                  <button
                    type="button"
                    className="bg-slate-700 hover:bg-slate-600 text-slate-200 px-4 py-2 rounded-md transition disabled:opacity-50"
                    onClick={handleCustomerLoadMore}
                    disabled={loadingCustomerEvents}
                  >
                    {loadingCustomerEvents ? 'Loading...' : 'Load more'}
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {!loadingReference && activeTab === 'product' && (
          <section className="space-y-6">
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 shadow-lg space-y-4">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <div>
          <label className="block text-sm font-medium text-slate-400 mb-2">
                    Product
                  </label>
                  <Select
                    options={productOptions}
                    value={
                      selectedProductId
                        ? productOptions.find(
                            (option) => option.value === selectedProductId
                          )
                        : null
                    }
                    classNamePrefix="react-select"
                    styles={selectStyles}
                    onChange={(option) => {
                      setSelectedProductId(option?.value || '');
                      setProductFilters((prev) => ({
                        ...prev,
                        customerId: null,
                        apiKeyId: null
                      }));
                    }}
                    isClearable
                    placeholder="Select a product..."
                  />
                  {selectedProduct && (
                    <div className="mt-2 text-xs text-slate-400 flex items-center gap-2">
                      <span className="font-mono">Product ID: {selectedProduct.id}</span>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(selectedProduct.id)}
                        className="p-1 rounded hover:bg-slate-700 text-slate-500 hover:text-slate-300 transition"
                        title="Copy product ID"
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    Date range
                  </label>
                  <Select
                    options={DATE_PRESETS}
                    value={DATE_PRESETS.find(
                      (option) => option.value === productFilters.preset
                    )}
                    classNamePrefix="react-select"
                    styles={selectStyles}
                    onChange={handleProductPresetChange}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">
                      Start
                    </label>
                    <input
                      type="date"
                      className="w-full rounded bg-slate-900 border border-white/20 text-slate-100 px-3 py-2"
                      value={productFilters.customStart}
                      onChange={(event) =>
                        setProductFilters((prev) => ({
                          ...prev,
                          customStart: event.target.value
                        }))
                      }
                      disabled={productFilters.preset !== 'CUSTOM'}
                      max={productDateRange.endDate}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">
                      End
                    </label>
                    <input
                      type="date"
                      className="w-full rounded bg-slate-900 border border-slate-700 text-slate-100 px-3 py-2"
                      value={productFilters.customEnd}
                      onChange={(event) =>
                        setProductFilters((prev) => ({
                          ...prev,
                          customEnd: event.target.value
                        }))
                      }
                      disabled={productFilters.preset !== 'CUSTOM'}
                      max={toISODate(startOfToday())}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    Customer filter
                  </label>
                  <Select
                    options={productCustomerOptions}
                    value={
                      productFilters.customerId
                        ? productCustomerOptions.find(
                            (option) => option.value === productFilters.customerId
                          )
                        : null
                    }
                    classNamePrefix="react-select"
                    styles={selectStyles}
                    onChange={handleProductCustomerChange}
                    isClearable
                    placeholder="All customers"
                    isDisabled={!selectedProductId}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    API key filter
                  </label>
                  <Select
                    options={productKeyOptions}
                    value={
                      productFilters.apiKeyId
                        ? productKeyOptions.find(
                            (option) => option.value === productFilters.apiKeyId
                          )
                        : null
                    }
                    classNamePrefix="react-select"
                    styles={selectStyles}
                    onChange={handleProductKeyChange}
                    isClearable
                    placeholder="All keys"
                    isDisabled={!selectedProductId}
                  />
                </div>
              </div>
              <div className="text-sm text-slate-500">
                Reporting window: {productDateRange.startDate} to{' '}
                {productDateRange.endDate}
              </div>
            </div>

            {productError && (
              <div className="bg-red-500/20 border border-red-400/40 text-red-100 px-4 py-3 rounded">
                {productError}
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-4">
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                <p className="text-sm text-slate-500">Total units</p>
                <p className="text-3xl font-semibold">
                  {formatNumber(productSummary?.totals?.totalUnits || 0)}
                </p>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                <p className="text-sm text-slate-500">Total validations</p>
                <p className="text-3xl font-semibold">
                  {formatNumber(productSummary?.validationTotals?.totalValidations || 0)}
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  {formatNumber(productSummary?.validationTotals?.validCount || 0)} valid ·{' '}
                  {formatNumber(productSummary?.validationTotals?.invalidCount || 0)} invalid
                </p>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                <p className="text-sm text-slate-500">Top customer</p>
                <p className="text-lg font-semibold">
                  {productSummary?.totals?.customers?.[0]
                    ? `${productSummary.totals.customers[0].scope_name} · ${formatNumber(
                        productSummary.totals.customers[0].total_units
                      )} units`
                    : '-'}
                </p>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                <p className="text-sm text-slate-500">Top API key</p>
                <p className="text-lg font-semibold">
                  {productSummary?.totals?.apiKeys?.[0]
                    ? `${productSummary.totals.apiKeys[0].scope_name} · ${formatNumber(
                        productSummary.totals.apiKeys[0].total_units
                      )} units`
                    : '-'}
                </p>
              </div>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 space-y-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <h2 className="text-xl font-semibold">Usage trend</h2>
                <div className="flex items-center gap-3">
                  {productLastRefreshed && (
                    <span
                      className="text-xs text-slate-500"
                      title={productLastRefreshed.toLocaleString()}
                    >
                      as of{' '}
                      {productLastRefreshed.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  )}
                  <button
                    type="button"
                    className="bg-slate-700 hover:bg-slate-600 text-slate-200 px-4 py-2 rounded-md transition disabled:opacity-50"
                    onClick={() => {
                      loadProductOverview();
                      loadProductEvents(1, false);
                    }}
                    disabled={loadingProductOverview || loadingProductEvents}
                  >
                    Refresh
                  </button>
                  <button
                    type="button"
                    className="bg-indigo-600 text-white font-medium px-4 py-2 rounded-md transition hover:bg-indigo-500 disabled:opacity-50"
                    onClick={handleProductExport}
                    disabled={
                      !selectedProductId ||
                      productEvents.length === 0 ||
                      productExporting
                    }
                  >
                    {productExporting ? 'Exporting...' : 'Export CSV'}
                  </button>
                </div>
              </div>
              <div className="space-y-6">
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-slate-300">Usage</h3>
                    {(productSummary?.totals?.apiKeys?.length || 0) > 1 && (
                      <div className="inline-flex rounded-md bg-slate-800 p-1 self-start">
                        <button
                          type="button"
                          className={`px-3 py-1 rounded-md transition text-sm ${
                            !splitProductByKey
                              ? 'bg-indigo-600 text-white font-medium'
                              : 'text-slate-400 hover:text-slate-100'
                          }`}
                          onClick={() => setSplitProductByKey(false)}
                        >
                          Combined
                        </button>
                        <button
                          type="button"
                          className={`px-3 py-1 rounded-md transition text-sm ${
                            splitProductByKey
                              ? 'bg-indigo-600 text-white font-medium'
                              : 'text-slate-400 hover:text-slate-100'
                          }`}
                          onClick={() => setSplitProductByKey(true)}
                        >
                          Split by key
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="h-64">
                    {loadingProductOverview ? (
                      <div className="h-full flex items-center justify-center text-slate-400">
                        Loading chart...
                      </div>
                    ) : splitProductByKey && productSeriesByKey.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={productSeriesByKey}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
                          <XAxis dataKey="bucket" stroke="#ffffff80" />
                          <YAxis stroke="#ffffff80" />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#1f2240',
                              borderColor: '#ffffff40'
                            }}
                          />
                          <Legend />
                          {productKeyLegend.map((key) => (
                            <Line
                              key={key.id}
                              type="monotone"
                              dataKey={key.id}
                              name={key.nickname}
                              stroke={colorForApiKey(key.id)}
                              strokeWidth={2}
                              dot={false}
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    ) : productSeries.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={productSeries}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
                          <XAxis dataKey="date" stroke="#ffffff80" />
                          <YAxis stroke="#ffffff80" />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#1f2240',
                              borderColor: '#ffffff40'
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="units"
                            stroke="#93C5FD"
                            strokeWidth={3}
                            dot={{ r: 4 }}
                            name="Units"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-slate-500">
                        No usage recorded for this period.
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-300 mb-1">Validations</h3>
                  <p className="text-xs text-amber-400/80 mb-2">
                    Validation records are retained for ~24 hours — longer date ranges still
                    show only the most recent day.
                  </p>
                  <div className="h-64">
                    {loadingProductOverview ? (
                      <div className="h-full flex items-center justify-center text-slate-400">
                        Loading chart...
                      </div>
                    ) : productValidationSeries.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={productValidationSeries}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
                          <XAxis dataKey="date" stroke="#ffffff80" />
                          <YAxis stroke="#ffffff80" />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#1f2240',
                              borderColor: '#ffffff40'
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="validations"
                            stroke="#F97316"
                            strokeWidth={3}
                            dot={{ r: 4 }}
                            name="Validations"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-slate-500">
                        No validations recorded for this period.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 space-y-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <h2 className="text-xl font-semibold">Recent activity</h2>
                  <div className="inline-flex rounded-md bg-slate-800 p-1">
                    <button
                      type="button"
                      className={`px-3 py-1 rounded-md transition text-sm ${
                        productEventType === 'usage'
                          ? 'bg-indigo-600 text-white font-medium'
                          : 'text-slate-400 hover:text-slate-100'
                      }`}
                      onClick={() => setProductEventType('usage')}
                    >
                      Usage
                    </button>
                    <button
                      type="button"
                      className={`px-3 py-1 rounded-md transition text-sm ${
                        productEventType === 'validation'
                          ? 'bg-indigo-600 text-white font-medium'
                          : 'text-slate-400 hover:text-slate-100'
                      }`}
                      onClick={() => setProductEventType('validation')}
                    >
                      Validations
                    </button>
                    <button
                      type="button"
                      className={`px-3 py-1 rounded-md transition text-sm ${
                        productEventType === 'live'
                          ? 'bg-indigo-600 text-white font-medium'
                          : 'text-slate-400 hover:text-slate-100'
                      }`}
                      onClick={() => setProductEventType('live')}
                      title="Per-call events, held for 48h — useful for confirming a hit just landed."
                    >
                      Live
                    </button>
                  </div>
                </div>
                <span className="text-sm text-slate-500">
                  {productEventType === 'live' ? (
                    <>
                      {productLiveEvents.length} recent{' '}
                      {productLiveEvents.length === 1 ? 'event' : 'events'}
                      {productLiveUpdatedAt && (
                        <> · updated {relativeTime(productLiveUpdatedAt.toISOString())}</>
                      )}
                    </>
                  ) : (
                    <>
                      Showing {productEvents.length} of {productPagination.total}{' '}
                      {productEventType === 'validation' ? 'validation' : 'usage'} events
                    </>
                  )}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left">
                  <thead>
                    {productEventType === 'usage' ? (
                      <tr className="text-sm uppercase text-slate-500 border-b border-slate-700">
                        <th
                          className="py-3 pr-4"
                          title="Usage is aggregated into one row per key per UTC day; 'Recorded at' is the last time that day's row was updated, not a per-call timestamp."
                        >
                          Recorded at (UTC)
                        </th>
                        <th className="py-3 pr-4">Usage date (UTC)</th>
                        <th className="py-3 pr-4">Units</th>
                        <th className="py-3 pr-4">Customer team</th>
                        <th className="py-3 pr-4">Customer org</th>
                        <th className="py-3 pr-4">API key</th>
                        <th className="py-3 pr-4">Plan</th>
                      </tr>
                    ) : productEventType === 'validation' ? (
                      <tr className="text-sm uppercase text-slate-500 border-b border-slate-700">
                        <th className="py-3 pr-4">Validated at</th>
                        <th className="py-3 pr-4">Result</th>
                        <th className="py-3 pr-4">Customer team</th>
                        <th className="py-3 pr-4">Customer org</th>
                        <th className="py-3 pr-4">API key</th>
                        <th className="py-3 pr-4">Source</th>
                        <th className="py-3 pr-4">Plan</th>
                      </tr>
                    ) : (
                      <tr className="text-sm uppercase text-slate-500 border-b border-slate-700">
                        <th
                          className="py-3 pr-4"
                          title="Real per-call timestamps, unlike the aggregated Usage tab."
                        >
                          Occurred
                        </th>
                        <th className="py-3 pr-4">API key</th>
                        <th className="py-3 pr-4">Units</th>
                      </tr>
                    )}
                  </thead>
                  <tbody className="divide-y divide-white/10 text-sm">
                    {productEventType === 'usage'
                      ? productEvents.map((event) => (
                          <tr key={event.id} className="hover:bg-slate-800">
                            <td className="py-3 pr-4 whitespace-nowrap text-slate-300">
                              {event.createdAt
                                ? new Date(event.createdAt).toLocaleString()
                                : '-'}
                            </td>
                            <td className="py-3 pr-4 text-slate-400">{event.usageDate}</td>
                            <td className="py-3 pr-4 text-slate-100">
                              {formatNumber(event.units)}
                            </td>
                            <td className="py-3 pr-4 text-slate-300">
                              {event.customer?.teamName || '-'}
                            </td>
                            <td className="py-3 pr-4 text-slate-400">
                              {event.customer?.organizationName || '-'}
                            </td>
                            <td className="py-3 pr-4 text-slate-400">
                              {event.apiKey?.nickname || event.apiKey?.id || '-'}
                            </td>
                            <td className="py-3 pr-4 text-slate-400">
                              {event.plan?.name || '-'}
                            </td>
                          </tr>
                        ))
                      : productEventType === 'validation'
                      ? productEvents.map((event) => (
                          <tr key={event.id} className="hover:bg-slate-800">
                            <td className="py-3 pr-4 whitespace-nowrap text-slate-300">
                              {event.validatedAt
                                ? new Date(event.validatedAt).toLocaleString()
                                : '-'}
                            </td>
                            <td className="py-3 pr-4 text-slate-100">
                              {event.isValid ? 'Valid' : 'Rejected'}
                            </td>
                            <td className="py-3 pr-4 text-slate-300">
                              {event.customer?.teamName || '-'}
                            </td>
                            <td className="py-3 pr-4 text-slate-400">
                              {event.customer?.organizationName || '-'}
                            </td>
                            <td className="py-3 pr-4 text-slate-400">
                              {event.apiKey?.nickname || event.apiKey?.id || '-'}
                            </td>
                            <td className="py-3 pr-4 text-slate-400">
                              {event.source || '-'}
                            </td>
                            <td className="py-3 pr-4 text-slate-400">
                              {event.plan?.name || '-'}
                            </td>
                          </tr>
                        ))
                      : productLiveEvents.map((event) => (
                          <tr key={event.id} className="hover:bg-slate-800">
                            <td className="py-3 pr-4 whitespace-nowrap text-slate-300">
                              {relativeTime(event.occurredAt)}
                            </td>
                            <td className="py-3 pr-4 text-slate-400">
                              {event.apiKeyId}
                            </td>
                            <td className="py-3 pr-4 text-slate-100">
                              {formatNumber(event.units)}
                            </td>
                          </tr>
                        ))}
                    {productEventType !== 'live' &&
                      productEvents.length === 0 &&
                      !loadingProductEvents && (
                        <tr>
                          <td
                            colSpan={7}
                            className="py-6 text-center text-slate-500"
                          >
                            No {productEventType === 'validation' ? 'validation' : 'usage'} events recorded for this selection.
                          </td>
                        </tr>
                      )}
                    {productEventType === 'live' && productLiveEvents.length === 0 && (
                      <tr>
                        <td colSpan={3} className="py-6 text-center text-slate-500">
                          Waiting for activity — events appear here within a few seconds of the call.
                        </td>
                      </tr>
                    )}
                    {productEventType !== 'live' && loadingProductEvents && (
                      <tr>
                        <td
                          colSpan={7}
                          className="py-6 text-center text-slate-500"
                        >
                          Loading events...
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {productEventType !== 'live' && hasMoreProductEvents && (
                <div className="flex justify-center">
                  <button
                    type="button"
                    className="bg-slate-700 hover:bg-slate-600 text-slate-200 px-4 py-2 rounded-md transition disabled:opacity-50"
                    onClick={handleProductLoadMore}
                    disabled={loadingProductEvents}
                  >
                    {loadingProductEvents ? 'Loading...' : 'Load more'}
                  </button>
                </div>
              )}
            </div>
          </section>
        )}
    </div>
  );
}
